import Cookies from 'js-cookie';

export interface PWAUser {
  id: string;
  name: string;
  email: string;
  token: string;
  lastLogin: Date;
  biometricEnabled: boolean;
}

export interface BiometricCredentials {
  id: string;
  name: string;
  displayName: string;
}

class PWAAuthService {
  private readonly STORAGE_KEY = 'pwa_user_session';
  private readonly BIOMETRIC_KEY = 'pwa_biometric_enabled';
  private readonly REMEMBER_ME_KEY = 'pwa_remember_me';

  // Check if WebAuthn is supported
  isWebAuthnSupported(): boolean {
    return window.PublicKeyCredential !== undefined;
  }

  // Check if biometric authentication is available
  async isBiometricAvailable(): Promise<boolean> {
    if (!this.isWebAuthnSupported()) {
      return false;
    }

    try {
      return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch (error) {
      console.error('Error checking biometric availability:', error);
      return false;
    }
  }

  // Enable biometric authentication for current user
  async enableBiometric(user: PWAUser): Promise<boolean> {
    if (!this.isWebAuthnSupported()) {
      throw new Error('WebAuthn not supported');
    }

    try {
      // Create biometric credentials
      const publicKeyOptions: PublicKeyCredentialCreationOptions = {
        challenge: new Uint8Array(32),
        rp: {
          name: 'TimeTracker',
          id: window.location.hostname,
        },
        user: {
          id: new Uint8Array(16),
          name: user.email,
          displayName: user.name,
        },
        pubKeyCredParams: [
          {
            type: 'public-key',
            alg: -7, // ES256
          },
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required',
        },
        timeout: 60000,
        attestation: 'direct',
      };

      const credential = await navigator.credentials.create({
        publicKey: publicKeyOptions,
      });

      if (credential) {
        // Store biometric enabled flag
        localStorage.setItem(this.BIOMETRIC_KEY, 'true');
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error enabling biometric:', error);
      return false;
    }
  }

  // Authenticate using biometric
  async authenticateWithBiometric(): Promise<PWAUser | null> {
    if (!this.isWebAuthnSupported()) {
      throw new Error('WebAuthn not supported');
    }

    try {
      const publicKeyOptions: PublicKeyCredentialRequestOptions = {
        challenge: new Uint8Array(32),
        rpId: window.location.hostname,
        userVerification: 'required',
        timeout: 60000,
      };

      const assertion = await navigator.credentials.get({
        publicKey: publicKeyOptions,
      });

      if (assertion) {
        // Retrieve stored user data
        const userData = this.getStoredUser();
        if (userData) {
          return userData;
        }
      }

      return null;
    } catch (error) {
      console.error('Error with biometric authentication:', error);
      return null;
    }
  }

  // Store user session securely
  storeUserSession(user: PWAUser, rememberMe: boolean = false): void {
    const sessionData = {
      ...user,
      lastLogin: new Date().toISOString(),
    };

    // Store in localStorage for PWA persistence
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(sessionData));
    
    // Set remember me preference
    localStorage.setItem(this.REMEMBER_ME_KEY, rememberMe.toString());

    // Also store token in cookies for API compatibility
    Cookies.set('U_EVAL_VALUE', user.token, {
      expires: rememberMe ? 30 : 1, // 30 days if remember me, 1 day otherwise
      secure: true,
      sameSite: 'strict'
    });
  }

  // Get stored user session
  getStoredUser(): PWAUser | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const userData = JSON.parse(stored);
        return {
          ...userData,
          lastLogin: new Date(userData.lastLogin)
        };
      }
    } catch (error) {
      console.error('Error retrieving stored user:', error);
    }
    return null;
  }

  // Check if user has biometric enabled
  isBiometricEnabled(): boolean {
    return localStorage.getItem(this.BIOMETRIC_KEY) === 'true';
  }

  // Check if remember me is enabled
  isRememberMeEnabled(): boolean {
    return localStorage.getItem(this.REMEMBER_ME_KEY) === 'true';
  }

  // Clear user session
  clearUserSession(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.BIOMETRIC_KEY);
    localStorage.removeItem(this.REMEMBER_ME_KEY);
    Cookies.remove('U_EVAL_VALUE');
  }

  // Auto-login logic
  async attemptAutoLogin(): Promise<PWAUser | null> {
    const storedUser = this.getStoredUser();
    
    if (!storedUser) {
      return null;
    }

    // Check if remember me is enabled
    if (!this.isRememberMeEnabled()) {
      // Check if session is still valid (within 24 hours)
      const hoursSinceLogin = (Date.now() - storedUser.lastLogin.getTime()) / (1000 * 60 * 60);
      if (hoursSinceLogin > 24) {
        this.clearUserSession();
        return null;
      }
    }

    // If biometric is enabled, try biometric authentication
    if (this.isBiometricEnabled()) {
      try {
        const biometricUser = await this.authenticateWithBiometric();
        if (biometricUser) {
          return biometricUser;
        }
      } catch (error) {
        console.error('Biometric authentication failed:', error);
      }
    }

    // Return stored user if no biometric or biometric failed
    return storedUser;
  }

  // Check if app is installed as PWA
  isPWAInstalled(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches ||
           (window.navigator as any).standalone === true;
  }

  // Get PWA installation status
  getPWAInstallStatus(): 'installed' | 'not-installed' | 'not-supported' {
    if (!('serviceWorker' in navigator)) {
      return 'not-supported';
    }
    
    if (this.isPWAInstalled()) {
      return 'installed';
    }
    
    return 'not-installed';
  }
}

export const pwaAuthService = new PWAAuthService();
export default pwaAuthService; 